import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';

// Config and utilities
import { API_URL, downloadAuthenticatedFile } from './config/api';

// Native Mobile Capacitor Bootstrap
import { useNativeBootstrap } from './services/native';

// Custom Hooks
import { useAuth } from './hooks/useAuth';
import { useTickets } from './hooks/useTickets';
import { useAssets } from './hooks/useAssets';
import { useClients } from './hooks/useClients';
import { useTeams } from './hooks/useTeams';
import { useMessages } from './hooks/useMessages';
import { useReports } from './hooks/useReports';
import { useSubscription } from './hooks/useSubscription';
import { useUsers } from './hooks/useUsers';

// Navigation & Global Modals
import Sidebar from './components/navigation/Sidebar';
import Header from './components/navigation/Header';
import LoginView from './components/auth/LoginView';
import PrepaidActivationPortal from './components/PrepaidActivationPortal';
import GlobalSearchModal from './components/GlobalSearchModal';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import ProfileModal from './components/modals/ProfileModal';
import SubscriptionModal from './components/modals/SubscriptionModal';
import HelpModal from './components/modals/HelpModal';
import TicketEditModal from './components/modals/TicketEditModal';
import CloseTicketModal from './components/modals/CloseTicketModal';
import TicketDetailModal from './components/modals/TicketDetailModal';
import TicketNoteModal from './components/modals/TicketNoteModal';
import AssetDetailModal from './components/modals/AssetDetailModal';
import EditAssetModal from './components/modals/EditAssetModal';
import PasswordResetModal from './components/modals/PasswordResetModal';
import EditTeamModal from './components/modals/EditTeamModal';
import AddTeamMemberModal from './components/modals/AddTeamMemberModal';

import LoadingSpinner from './components/LoadingSpinner';

// Core Primary Views
import ModernDashboard from './components/ModernDashboard';
import TicketsManagement from './components/TicketsManagement';
import AssetsManagement from './components/AssetsManagement';
import ImpersonationBanner from './components/ImpersonationBanner';
import MobileBottomNav from './components/navigation/MobileBottomNav';

// Resilient lazy loader with automatic cache recovery for Webpack chunks
const lazyWithRetry = (componentImport) =>
  React.lazy(async () => {
    try {
      return await componentImport();
    } catch (error) {
      const isChunkError = error?.name === 'ChunkLoadError' || /Loading chunk/i.test(error?.message);
      if (isChunkError && typeof window !== 'undefined') {
        const lastReload = sessionStorage.getItem('chunk_retry_timestamp');
        const now = Date.now();
        if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
          sessionStorage.setItem('chunk_retry_timestamp', String(now));
          window.location.reload();
          return new Promise(() => {});
        }
      }
      throw error;
    }
  });

// Code-Split / Lazy-Loaded Secondary & Heavy Administrative Modules
const ClosedTicketsManagement = lazyWithRetry(() => import('./components/ClosedTicketsManagement'));
const ModernClientsManagement = lazyWithRetry(() => import('./components/ModernClientsManagement'));
const SuperAdminSettings = lazyWithRetry(() => import('./components/SuperAdminSettings'));
const ModernTeamChat = lazyWithRetry(() => import('./components/ModernTeamChat'));
const ModernTeamsManagement = lazyWithRetry(() => import('./components/ModernTeamsManagement'));
const ModernUsersManagement = lazyWithRetry(() => import('./components/ModernUsersManagement'));
const ModernAnalytics = lazyWithRetry(() => import('./components/ModernAnalytics'));
const ModernInfrastructure = lazyWithRetry(() => import('./components/ModernInfrastructure'));
const ModernReports = lazyWithRetry(() => import('./components/ModernReports'));
const ModernGroupsManagement = lazyWithRetry(() => import('./components/ModernGroupsManagement'));
const ModernWebhooksManagement = lazyWithRetry(() => import('./components/ModernWebhooksManagement'));
const SolutionsManagement = lazyWithRetry(() => import('./components/SolutionsManagement'));
const CustomerPortalView = lazyWithRetry(() => import('./components/portal/CustomerPortalView'));
const SuperAdminSecuritySuite = lazyWithRetry(() => import('./components/SuperAdminSecuritySuite'));
const SubscriptionPlansManagement = lazyWithRetry(() => import('./components/SubscriptionPlansManagement'));
const SuperAdminBulkPush = lazyWithRetry(() => import('./components/SuperAdminBulkPush'));
const TenantSubscriptionView = lazyWithRetry(() => import('./components/TenantSubscriptionView'));
const TenantEmailSettings = lazyWithRetry(() => import('./components/TenantEmailSettings'));

const App = () => {
  // Mobile Native Lifecycle & Native Feature Bootstrap
  useNativeBootstrap();

  // Global message / toast state
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [messageTimeout, setMessageTimeout] = useState(null);

  const showMessage = useCallback((msg, type = 'success') => {
    setMessage(msg);
    setMessageType(type);
    if (messageTimeout) clearTimeout(messageTimeout);
    const timeout = setTimeout(() => {
      setMessage('');
    }, 4000);
    setMessageTimeout(timeout);
  }, [messageTimeout]);

  // Layout and view state
  const [currentView, setCurrentView] = useState('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  const [isSidebarPinned, setIsSidebarPinned] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('sidebar_pinned');
      return saved !== null ? JSON.parse(saved) : true;
    }
    return true;
  });
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  const [isMainScrolling, setIsMainScrolling] = useState(false);
  const mainScrollTimeoutRef = useRef(null);

  const handleMainScroll = () => {
    setIsMainScrolling(true);
    if (mainScrollTimeoutRef.current) clearTimeout(mainScrollTimeoutRef.current);
    mainScrollTimeoutRef.current = setTimeout(() => {
      setIsMainScrolling(false);
    }, 1000);
  };

  // Modals state
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [isGlobalSearchOpen, setIsGlobalSearchOpen] = useState(false);

  // Auth Hook
  const auth = useAuth(showMessage);
  const { user, token, loading } = auth;

  // Impersonation state
  const [impersonationData, setImpersonationData] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('active_impersonation');
      return saved ? JSON.parse(saved) : null;
    }
    return null;
  });
  const [isExitingImpersonation, setIsExitingImpersonation] = useState(false);

  const handleStartImpersonation = (targetToken, targetUser) => {
    const originalSession = { token, user };
    localStorage.setItem('original_superadmin_session', JSON.stringify(originalSession));
    localStorage.setItem('active_impersonation', JSON.stringify(targetUser));
    localStorage.setItem('token', targetToken);
    setImpersonationData(targetUser);
    auth.setToken(targetToken);
    auth.setUser(targetUser);
    setCurrentView('dashboard');
  };

  const handleExitImpersonation = async () => {
    setIsExitingImpersonation(true);
    try {
      const savedOriginal = localStorage.getItem('original_superadmin_session');
      if (savedOriginal) {
        const parsed = JSON.parse(savedOriginal);
        localStorage.setItem('token', parsed.token);
        localStorage.removeItem('original_superadmin_session');
        localStorage.removeItem('active_impersonation');
        setImpersonationData(null);
        auth.setToken(parsed.token);
        auth.setUser(parsed.user);
        setCurrentView('superadmin-security');
        showMessage('Restored SuperAdmin Platform Control session.', 'success');
      }
    } finally {
      setIsExitingImpersonation(false);
    }
  };

  // Domain Hooks
  const tickets = useTickets(user, token, showMessage);
  const assets = useAssets(user, token, showMessage);
  const clients = useClients(user, token, showMessage);
  const teams = useTeams(user, token, tickets.assignableUsers, showMessage);
  const messages = useMessages(user, token, showMessage);
  const reports = useReports(user, token);
  const subscription = useSubscription(token, showMessage);
  const users = useUsers(user, token, showMessage);

  // Window resize listener
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Global Command Search (⌘K / Ctrl+K) shortcut listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsGlobalSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Initial data fetch on login
  useEffect(() => {
    if (user && token) {
      tickets.fetchTickets();
      tickets.fetchDashboardStats();
      tickets.fetchAssignableUsers();
      subscription.fetchSubscriptionData();
    }
  }, [user, token]);

  // Contextual fetches on view changes
  useEffect(() => {
    if (!user || !token) return;

    if (currentView === 'dashboard') {
      tickets.fetchDashboardStats();
      reports.fetchDailyWorkData();
    } else if (currentView === 'tickets' || currentView === 'create-ticket') {
      tickets.fetchTickets();
    } else if (currentView === 'closed-tickets') {
      tickets.fetchClosedTickets();
    } else if (currentView === 'assets' || currentView === 'create-asset') {
      assets.fetchAssets();
    } else if (currentView === 'clients' && user.role === 'superadmin') {
      clients.fetchClients();
    } else if (currentView === 'chat') {
      messages.fetchMessages();
    } else if (currentView === 'teams' || currentView === 'create-team' || currentView === 'manage-groups') {
      teams.fetchTeams();
    } else if (currentView === 'users' || currentView === 'create-user') {
      users.fetchUsers();
    } else if (currentView === 'analytics') {
      reports.fetchAnalytics();
      reports.fetchDailyWorkData();
    } else if (currentView === 'reports') {
      reports.fetchReportsData();
    }
  }, [currentView, user, token]);

  // Loading spinner during session validation
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  // Public Customer Portal / Ticket Tracking Routes (No agent login required)
  const pathname = window.location.pathname;
  const portalMatch = pathname.match(/^\/portal\/([a-zA-Z0-9_-]+)/);
  const trackMatch = pathname.match(/^\/track\/([a-zA-Z0-9_-]+)\/([a-zA-Z0-9_-]+)/);

  if (portalMatch || trackMatch) {
    const urlParams = new URLSearchParams(window.location.search);
    const trackingToken = urlParams.get('token');
    const clientCode = portalMatch ? portalMatch[1] : trackMatch[1];
    const ticketId = trackMatch ? trackMatch[2] : (urlParams.get('ticket_id') || '');

    return (
      <CustomerPortalView
        clientCode={clientCode}
        initialTicketId={ticketId}
        trackingToken={trackingToken}
      />
    );
  }

  // Render solid corporate login if unauthenticated
  if (!user) {
    return (
      <LoginView
        loginData={auth.loginData}
        setLoginData={auth.setLoginData}
        loginError={auth.loginError}
        isLoading={auth.isLoadingAuth}
        handleLogin={auth.handleLogin}
        requiresTOTP={auth.requiresTOTP}
        requiresTOTPSetup={auth.requiresTOTPSetup}
        requiresPasswordChange={auth.requiresPasswordChange}
        setRequiresPasswordChange={auth.setRequiresPasswordChange}
        forcePasswordForm={auth.forcePasswordForm}
        setForcePasswordForm={auth.setForcePasswordForm}
        handleForcePasswordChange={auth.handleForcePasswordChange}
        totpToken={auth.totpToken}
        setTotpToken={auth.setTotpToken}
        backupCode={auth.backupCode}
        setBackupCode={auth.setBackupCode}
        useBackupCode={auth.useBackupCode}
        setUseBackupCode={auth.setUseBackupCode}
        totpSetupData={auth.totpSetupData}
        setupTOTP={auth.setupTOTP}
        verifyTOTPSetup={auth.verifyTOTPSetup}
        resetTOTP={auth.resetTOTP}
      />
    );
  }

  // Standalone Full-Screen Prepaid Activation Page (Stops user before entering CRM workspace)
  const isPrepaidPending = (
    (user?.requires_payment ||
      user?.plan_status === 'pending_payment' ||
      subscription?.subscriptionData?.requires_payment ||
      subscription?.subscriptionData?.plan_status === 'pending_payment') &&
    user?.role !== 'superadmin'
  );

  if (isPrepaidPending) {
    return (
      <PrepaidActivationPortal
        user={user}
        subscriptionData={subscription?.subscriptionData}
        onLogout={auth.handleLogout}
        onActivationSuccess={async () => {
          await subscription.fetchSubscriptionData();
          await auth.checkAuth();
        }}
        showMessage={showMessage}
      />
    );
  }

  const isChatOrTeams = currentView === 'chat' || currentView === 'teams';

  return (
    <div className="min-h-screen bg-slate-100/70 flex relative font-sans text-slate-800">
      {/* PWA Prompt */}
      <PWAInstallPrompt />

      {/* Global Message Toast */}
      {message && (
        <div
          className={`fixed bottom-5 right-5 z-50 px-4 py-3 rounded-xl shadow-lg border flex items-center gap-3 animate-fadeIn text-xs font-bold ${
            messageType === 'error'
              ? 'bg-red-50 border-red-200 text-red-700'
              : messageType === 'info'
              ? 'bg-blue-50 border-blue-200 text-blue-700'
              : 'bg-emerald-50 border-emerald-200 text-emerald-700'
          }`}
        >
          <span>{message}</span>
          <button onClick={() => setMessage('')} className="ml-1 opacity-70 hover:opacity-100">
            ✕
          </button>
        </div>
      )}

      {/* Navigation Sidebar */}
      <Sidebar
        user={user}
        currentView={currentView}
        setCurrentView={setCurrentView}
        isMobile={isMobile}
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        isSidebarPinned={isSidebarPinned}
        setIsSidebarPinned={setIsSidebarPinned}
        isSidebarHovered={isSidebarHovered}
        setIsSidebarHovered={setIsSidebarHovered}
      />

      {/* Main Workspace Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Impersonation Alert Banner */}
        <ImpersonationBanner
          impersonationData={impersonationData}
          onExitImpersonation={handleExitImpersonation}
          isExiting={isExitingImpersonation}
        />

        {/* Navigation Header */}
        <Header
          user={user}
          subscriptionData={subscription.subscriptionData}
          isMobile={isMobile}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          setIsGlobalSearchOpen={setIsGlobalSearchOpen}
          showProfileDropdown={showProfileDropdown}
          setShowProfileDropdown={setShowProfileDropdown}
          setShowProfileModal={setShowProfileModal}
          setShowSubscriptionModal={setShowSubscriptionModal}
          setShowHelpModal={setShowHelpModal}
          fetchSubscriptionData={subscription.fetchSubscriptionData}
          handleLogout={auth.handleLogout}
          showMessage={showMessage}
        />

        {/* Viewport Content */}
        <main
          onScroll={handleMainScroll}
          className={`flex-1 main-scroll ${isMainScrolling ? 'is-scrolling' : ''} ${
            isChatOrTeams
              ? 'h-[calc(100vh-64px)] overflow-hidden flex flex-col bg-white'
              : 'overflow-y-auto bg-[#F4F7FB]'
          }`}
        >
          <div
            className={
              isChatOrTeams
                ? 'w-full h-full flex-1 flex flex-col p-0 m-0 max-w-none'
                : 'max-w-7xl mx-auto px-3 sm:px-4 md:px-6 pt-2.5 md:pt-3 pb-20 md:pb-8'
            }
          >
            <React.Suspense fallback={<div className="flex items-center justify-center min-h-[400px]"><LoadingSpinner size="lg" text="Loading..." /></div>}>
            {/* 7-Day Pre-Expiry Reminder Banner */}
            {user?.is_renewal_due && !user?.is_expired && currentView !== 'subscription' && (
              <div className="mb-4 p-3.5 bg-amber-50 border border-amber-300 rounded-2xl flex items-center justify-between gap-3 shadow-2xs">
                <div className="flex items-center gap-2.5">
                  <span className="text-base">⚠️</span>
                  <div>
                    <span className="text-xs font-bold text-amber-900">
                      Subscription Notice: Your NEXDESK subscription expires in {user?.days_until_expiry} day(s).
                    </span>
                    <span className="text-[11px] text-amber-700 block sm:inline sm:ml-1">
                      Renew now to maintain uninterrupted access.
                    </span>
                  </div>
                </div>
                {user?.role === 'admin' && (
                  <button
                    onClick={() => setCurrentView('subscription')}
                    className="px-3.5 py-1.5 bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs rounded-xl shadow-2xs transition-colors flex-shrink-0 cursor-pointer"
                  >
                    Renew with Cashfree
                  </button>
                )}
              </div>
            )}

            {user?.is_expired && user?.role !== 'superadmin' && currentView !== 'subscription' ? (
              <div className="bg-white rounded-3xl p-8 border border-red-200 text-center max-w-lg mx-auto mt-12 space-y-4 shadow-sm">
                <div className="w-14 h-14 rounded-2xl bg-red-50 text-red-600 border border-red-200 flex items-center justify-center mx-auto text-2xl font-bold">
                  🔒
                </div>
                <h2 className="text-lg font-black text-slate-900">Organization Subscription Expired</h2>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Your organization's subscription has ended. Workspace operations and data creation are currently paused.
                </p>
                {user?.role === 'admin' ? (
                  <div className="pt-2 space-y-2">
                    <button
                      onClick={() => setCurrentView('subscription')}
                      className="w-full py-3 bg-red-600 hover:bg-red-700 text-white font-bold text-xs rounded-xl shadow-sm transition-all cursor-pointer flex items-center justify-center gap-2"
                    >
                      <span>Pay & Renew Now (Cashfree)</span>
                    </button>
                    <p className="text-[11px] text-slate-400">Instant activation on payment</p>
                  </div>
                ) : (
                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-[11px] text-slate-600">
                    Please contact your Organization Administrator to renew the subscription.
                  </div>
                )}
              </div>
            ) : (
              <>
            {/* Dashboard View */}
            {currentView === 'dashboard' && (
              <ModernDashboard
                user={user}
                dashboardStats={tickets.dashboardStats}
                tickets={tickets.tickets}
                closedTickets={tickets.closedTickets}
                assets={assets.assets}
                users={users.users}
                dailyWorkData={reports.dailyWorkData}
                setCurrentView={setCurrentView}
              />
            )}

            {/* Tickets Management View */}
            {(currentView === 'tickets' || currentView === 'create-ticket') && (
              <TicketsManagement
                tickets={tickets.tickets}
                ticketFilters={tickets.ticketFilters}
                setTicketFilters={tickets.setTicketFilters}
                fetchTickets={tickets.fetchTickets}
                assignableUsers={tickets.assignableUsers}
                handleTicketClick={tickets.handleTicketClick}
                setSelectedTicket={tickets.setSelectedTicket}
                setTicketToClose={tickets.setTicketToClose}
                setShowCloseModal={tickets.setShowCloseModal}
                setTicketForNote={tickets.setTicketForNote}
                setShowNoteModal={tickets.setShowNoteModal}
                downloadFile={downloadAuthenticatedFile}
                API_URL={API_URL}
                user={user}
                ticketForm={tickets.ticketForm}
                setTicketForm={tickets.setTicketForm}
                selectedFile={tickets.selectedFile}
                setSelectedFile={tickets.setSelectedFile}
                handleCreateTicket={tickets.handleCreateTicket}
                initialOpenCreate={currentView === 'create-ticket'}
              />
            )}

            {/* Closed Tickets View */}
            {currentView === 'closed-tickets' && (
              <ClosedTicketsManagement
                closedTickets={tickets.closedTickets}
                ticketFilters={tickets.ticketFilters}
                setTicketFilters={tickets.setTicketFilters}
                fetchClosedTickets={tickets.fetchClosedTickets}
                assignableUsers={tickets.assignableUsers}
                handleTicketClick={tickets.handleTicketClick}
                downloadFile={downloadAuthenticatedFile}
                API_URL={API_URL}
                user={user}
              />
            )}

            {/* Solutions / Knowledge Base Hub */}
            {currentView === 'solutions' && (
              <SolutionsManagement
                user={user}
                API_URL={API_URL}
                showMessage={showMessage}
              />
            )}

            {/* Hardware Assets Management View */}
            {(currentView === 'assets' || currentView === 'create-asset') && (
              <AssetsManagement
                assets={assets.assets}
                user={user}
                assignableUsers={tickets.assignableUsers}
                handleAssetClick={assets.handleAssetClick}
                handleCreateAsset={assets.handleCreateAsset}
                handleEditAsset={assets.handleEditAsset}
                handleDeleteAsset={assets.handleDeleteAsset}
                handleAssignAsset={assets.handleAssignAsset}
                downloadFile={downloadAuthenticatedFile}
                API_URL={API_URL}
                assetForm={assets.assetForm}
                setAssetForm={assets.setAssetForm}
                editingAsset={assets.editingAsset}
                setEditingAsset={assets.setEditingAsset}
                editAssetForm={assets.editAssetForm}
                setEditAssetForm={assets.setEditAssetForm}
                initialOpenCreate={currentView === 'create-asset'}
              />
            )}

            {/* SuperAdmin Tenants & Quota Management View */}
            {currentView === 'clients' && user.role === 'superadmin' && (
              <ModernClientsManagement
                user={user}
                clients={clients.clients}
                clientStats={clients.clientStats}
                showInactiveClients={clients.showInactiveClients}
                setShowInactiveClients={clients.setShowInactiveClients}
                clientForm={clients.clientForm}
                setClientForm={clients.setClientForm}
                handleCreateClient={clients.handleCreateClient}
                editingClient={clients.editingClient}
                setEditingClient={clients.setEditingClient}
                editClientForm={clients.editClientForm}
                setEditClientForm={clients.setEditClientForm}
                handleEditClient={clients.handleEditClient}
                editingUserLimit={clients.editingUserLimit}
                setEditingUserLimit={clients.setEditingUserLimit}
                userLimitForm={clients.userLimitForm}
                setUserLimitForm={clients.setUserLimitForm}
                assetLimitForm={clients.assetLimitForm}
                setAssetLimitForm={clients.setAssetLimitForm}
                handleUpdateAssetLimit={clients.handleUpdateAssetLimit}
                handleDeleteClient={clients.handleDeleteClient}
                handleSoftDeleteClient={clients.handleSoftDeleteClient}
                handleExportClientData={clients.handleExportClientData}
                handlePermanentlyDeleteClient={clients.handlePermanentlyDeleteClient}
                handleReactivateClient={clients.handleReactivateClient}
                isLoading={clients.isLoadingClients}
              />
            )}

            {/* SuperAdmin Subscription Plans & Packages View */}
            {currentView === 'subscription-plans' && user.role === 'superadmin' && (
              <SubscriptionPlansManagement
                token={token}
                user={user}
                showMessage={showMessage}
              />
            )}

            {/* SuperAdmin Security & Governance Suite View */}
            {currentView === 'superadmin-security' && user.role === 'superadmin' && (
              <SuperAdminSecuritySuite
                token={token}
                user={user}
                showMessage={showMessage}
                onStartImpersonation={handleStartImpersonation}
              />
            )}

            {/* SuperAdmin Platform Settings View */}
            {currentView === 'superadmin-settings' && user.role === 'superadmin' && (
              <SuperAdminSettings API_URL={API_URL} token={token} user={user} showMessage={showMessage} />
            )}

            {/* SuperAdmin Bulk Push Notifications View */}
            {currentView === 'superadmin-push' && user.role === 'superadmin' && (
              <SuperAdminBulkPush showMessage={showMessage} currentUser={user} />
            )}

            {/* Direct Team Chat View */}
            {currentView === 'chat' && (
              <ModernTeamChat
                user={user}
                assignableUsers={tickets.assignableUsers}
                messages={messages.messages}
                readMessages={messages.readMessages}
                selectedChatUser={messages.selectedChatUser}
                setSelectedChatUser={messages.setSelectedChatUser}
                handleSelectChatUser={messages.handleSelectChatUser}
                handleSendMessage={messages.handleSendMessage}
                newMessage={messages.newMessage}
                setNewMessage={messages.setNewMessage}
                selectedFile={messages.selectedFile}
                setSelectedFile={messages.setSelectedFile}
                isUploading={messages.isUploading}
                isLoading={messages.isLoadingMessages}
                getConversationMessages={messages.getConversationMessages}
                getLastMessage={messages.getLastMessage}
                API_URL={API_URL}
                isMobile={isMobile}
              />
            )}

            {/* Teams Management View */}
            {(currentView === 'teams' || currentView === 'create-team') && (
              <ModernTeamsManagement
                user={user}
                teams={teams.teams}
                selectedTeam={teams.selectedTeam}
                setSelectedTeam={teams.setSelectedTeam}
                handleSelectTeam={teams.handleSelectTeam}
                teamMessages={teams.teamMessages}
                fetchTeamMessages={teams.fetchTeamMessages}
                newTeamMessage={teams.newTeamMessage}
                setNewTeamMessage={teams.setNewTeamMessage}
                handleSendTeamMessage={teams.handleSendTeamMessage}
                handleCreateTeam={teams.handleCreateTeam}
                teamForm={teams.teamForm}
                setTeamForm={teams.setTeamForm}
                handleEditTeam={teams.handleEditTeam}
                editingTeam={teams.editingTeam}
                setEditingTeam={teams.setEditingTeam}
                editTeamForm={teams.editTeamForm}
                setEditTeamForm={teams.setEditTeamForm}
                handleDeleteTeam={teams.handleDeleteTeam}
                handleRemoveTeamMember={teams.handleRemoveTeamMember}
                handleAddTeamMember={teams.handleAddTeamMember}
                isTeamMember={teams.isTeamMember}
                canManageTeam={teams.canManageTeam}
                getTeamMembers={teams.getTeamMembers}
                assignableUsers={tickets.assignableUsers}
                selectedFile={teams.selectedFile}
                setSelectedFile={teams.setSelectedFile}
                isUploading={teams.isUploading}
                isLoading={teams.isLoadingTeams}
                API_URL={API_URL}
                isMobile={isMobile}
              />
            )}

            {/* Users Management View */}
            {(currentView === 'users' || currentView === 'create-user') &&
              (user.role === 'admin' || user.role === 'superadmin') && (
                <ModernUsersManagement
                  user={user}
                  users={users.users}
                  visibleUsers={users.users.filter((u) => u.role !== 'superadmin')}
                  editingUser={users.editingUser}
                  setEditingUser={users.setEditingUser}
                  newUsername={users.newUsername}
                  setNewUsername={users.setNewUsername}
                  updateUsername={users.updateUsername}
                  userToReset={users.userToReset}
                  setUserToReset={users.setUserToReset}
                  showPasswordResetModal={users.showPasswordResetModal}
                  setShowPasswordResetModal={users.setShowPasswordResetModal}
                  newPassword={users.newPassword}
                  setNewPassword={users.setNewPassword}
                  handleResetPassword={users.handlePasswordReset}
                  deleteUser={users.deleteUser}
                  userForm={users.userForm}
                  setUserForm={users.setUserForm}
                  handleCreateUser={users.handleCreateUser}
                  isLoading={users.isLoadingUsers}
                  downloadFile={downloadAuthenticatedFile}
                  API_URL={API_URL}
                  initialOpenCreate={currentView === 'create-user'}
                />
              )}

            {/* Analytics View */}
            {currentView === 'analytics' && (
              <ModernAnalytics
                user={user}
                analyticsData={reports.analyticsData}
                priorityVolume={reports.priorityVolume}
                analyticsFrom={reports.analyticsFrom}
                setAnalyticsFrom={reports.setAnalyticsFrom}
                analyticsTo={reports.analyticsTo}
                setAnalyticsTo={reports.setAnalyticsTo}
                analyticsLoading={reports.analyticsLoading}
                isMobile={isMobile}
              />
            )}

            {/* IT Infrastructure Health View */}
            {currentView === 'infrastructure' && <ModernInfrastructure token={token} user={user} />}

            {/* Executive Reports View */}
            {currentView === 'reports' && (
              <ModernReports
                user={user}
                tickets={tickets.tickets}
                reportsData={reports.reportsData}
                selectedReport={reports.selectedReport}
                setSelectedReport={reports.setSelectedReport}
                reportsFrom={reports.reportsFrom}
                setReportsFrom={reports.setReportsFrom}
                reportsTo={reports.reportsTo}
                setReportsTo={reports.setReportsTo}
                autoRefresh={reports.autoRefresh}
                setAutoRefresh={reports.setAutoRefresh}
                fetchReportsData={reports.fetchReportsData}
                reportsLoading={reports.reportsLoading}
                downloadFile={downloadAuthenticatedFile}
                API_URL={API_URL}
                setCurrentView={setCurrentView}
                isMobile={isMobile}
              />
            )}

            {/* Group Administration View */}
            {currentView === 'manage-groups' &&
              (user.role === 'admin' || user.role === 'superadmin') && (
                <ModernGroupsManagement
                  user={user}
                  teams={teams.teams}
                  assignableUsers={tickets.assignableUsers}
                  handleCreateTeam={teams.handleCreateTeam}
                  teamForm={teams.teamForm}
                  setTeamForm={teams.setTeamForm}
                  handleEditTeam={teams.handleEditTeam}
                  editingTeam={teams.editingTeam}
                  setEditingTeam={teams.setEditingTeam}
                  editTeamForm={teams.editTeamForm}
                  setEditTeamForm={teams.setEditTeamForm}
                  handleDeleteTeam={teams.handleDeleteTeam}
                  handleRemoveTeamMember={teams.handleRemoveTeamMember}
                  handleAddTeamMember={teams.handleAddTeamMember}
                  getTeamMembers={teams.getTeamMembers}
                  canManageTeam={teams.canManageTeam}
                  isLoading={teams.isLoadingTeams}
                  setCurrentView={setCurrentView}
                />
              )}

            {/* Webhook Integrations & Custom Pricing View */}
            {currentView === 'webhooks' &&
              (user?.role === 'admin' || user?.role === 'superadmin') && (
                (user?.role === 'superadmin' || user?.webhook_enabled === true || (Array.isArray(user?.plan_features) && user.plan_features.some(f => /webhook/i.test(f)))) ? (
                  <ModernWebhooksManagement
                    token={token}
                    user={user}
                    showMessage={showMessage}
                    onOpenTicketDetail={(ticket) => {
                      tickets.setSelectedTicketDetail(ticket);
                      tickets.setShowTicketDetailModal(true);
                    }}
                  />
                ) : (
                  <div className="bg-white rounded-2xl p-8 border border-slate-200 text-center max-w-lg mx-auto mt-12 space-y-3">
                    <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 border border-amber-200 flex items-center justify-center mx-auto text-xl font-bold">
                      🔒
                    </div>
                    <h2 className="text-base font-bold text-slate-800">Webhook Integrations Not Included</h2>
                    <p className="text-xs text-slate-500">
                      Webhook Integrations & Custom Pricing APIs are not enabled on your organization's current subscription plan ({user?.plan_name || 'Standard'}).
                    </p>
                    <p className="text-[11px] text-slate-400">
                      Please contact your SuperAdmin to upgrade your plan and unlock webhook access.
                    </p>
                    <button
                      onClick={() => setCurrentView('dashboard')}
                      className="px-4 py-2 bg-slate-900 text-white font-bold text-xs rounded-xl shadow-xs hover:bg-slate-800 transition-colors cursor-pointer"
                    >
                      Return to Dashboard
                    </button>
                  </div>
                )
              )}

            {/* Tenant Billing & Subscription View */}
            {currentView === 'subscription' && (
              <TenantSubscriptionView
                token={token}
                user={user}
                showMessage={showMessage}
              />
            )}

            {/* Tenant Support Email & Templates View */}
            {currentView === 'email-settings' && (user?.role === 'admin' || user?.role === 'superadmin') && (
              <TenantEmailSettings
                token={token}
                user={user}
                showMessage={showMessage}
              />
            )}
              </>
            )}
            </React.Suspense>
          </div>
        </main>

        {/* Android Material 3 Mobile Bottom Navigation */}
        <MobileBottomNav
          currentView={currentView}
          setCurrentView={setCurrentView}
          user={user}
          openTicketsCount={(tickets?.tickets || []).filter(t => t.status !== 'closed').length}
          unreadMessagesCount={messages?.unreadCount || 0}
        />
      </div>

      {/* Global Command Search Modal */}
      <GlobalSearchModal
        isOpen={isGlobalSearchOpen}
        onClose={() => setIsGlobalSearchOpen(false)}
        token={token}
        apiUrl={API_URL}
        tenantName={user?.client_name || 'Nexius Digital'}
        onSelectTicket={(t) => {
          tickets.setTicketFilters((prev) => ({ ...prev, search: t.id || t.title }));
          tickets.setSelectedTicket(t);
          tickets.setSelectedTicketDetail(t);
          tickets.setShowTicketDetailModal(true);
          setCurrentView('tickets');
        }}
        onSelectAsset={(a) => {
          assets.handleAssetClick(a);
          setCurrentView('assets');
        }}
      />

      {/* Profile & Security Modal */}
      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        user={user}
        subscriptionData={subscription.subscriptionData}
        profilePasswordForm={auth.profilePasswordForm}
        setProfilePasswordForm={auth.setProfilePasswordForm}
        handleChangePassword={auth.handleChangePassword}
        isChangingPassword={auth.isChangingPassword}
      />

      {/* Subscription & Invoices Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        user={user}
        subscriptionData={subscription.subscriptionData}
        isLoadingSubscription={subscription.isLoadingSubscription}
        handleDownloadInvoice={subscription.handleDownloadInvoice}
        usersCount={users.users.length}
        assetsCount={assets.assets.length}
      />

      {/* Help & Documentation Modal */}
      <HelpModal isOpen={showHelpModal} onClose={() => setShowHelpModal(false)} />

      {/* Ticket Edit Quick Modal */}
      <TicketEditModal
        ticket={tickets.selectedTicket}
        onClose={() => tickets.setSelectedTicket(null)}
        assignableUsers={tickets.assignableUsers}
        handleUpdateTicket={tickets.handleUpdateTicket}
        isLoading={tickets.isLoadingTickets}
        setTicket={tickets.setSelectedTicket}
      />

      {/* Close Ticket Modal */}
      <CloseTicketModal
        isOpen={tickets.showCloseModal}
        ticket={tickets.ticketToClose}
        closeComment={tickets.closeComment}
        setCloseComment={tickets.setCloseComment}
        onClose={() => {
          tickets.setShowCloseModal(false);
          tickets.setCloseComment('');
          tickets.setTicketToClose(null);
        }}
        handleCloseTicket={tickets.handleCloseTicket}
        isLoading={tickets.isLoadingTickets}
      />

      {/* Ticket Detail Modal */}
      <TicketDetailModal
        isOpen={tickets.showTicketDetailModal}
        ticket={tickets.selectedTicketDetail}
        onAddNote={tickets.handleAddTicketNote}
        onClose={() => {
          tickets.setShowTicketDetailModal(false);
          tickets.setSelectedTicketDetail(null);
        }}
      />

      {/* Ticket Add Note Modal */}
      <TicketNoteModal
        isOpen={tickets.showNoteModal}
        ticket={tickets.ticketForNote}
        onAddNote={tickets.handleAddTicketNote}
        isLoading={tickets.isLoadingTickets}
        onClose={() => {
          tickets.setShowNoteModal(false);
          tickets.setTicketForNote(null);
        }}
      />

      {/* Asset Detail Modal */}
      <AssetDetailModal
        isOpen={assets.showAssetDetailModal}
        asset={assets.selectedAssetDetail}
        onClose={() => {
          assets.setShowAssetDetailModal(false);
          assets.setSelectedAssetDetail(null);
        }}
      />

      {/* Edit Asset Modal */}
      <EditAssetModal
        editingAsset={assets.editingAsset}
        setEditingAsset={assets.setEditingAsset}
        editAssetForm={assets.editAssetForm}
        setEditAssetForm={assets.setEditAssetForm}
        assignableUsers={tickets.assignableUsers}
        handleEditAsset={assets.handleEditAsset}
        selectedFile={assets.selectedFile}
        setSelectedFile={assets.setSelectedFile}
        isUploading={assets.isUploading}
        isLoading={assets.isLoadingAssets}
      />

      {/* Password Reset Modal */}
      <PasswordResetModal
        isOpen={users.showPasswordResetModal}
        userToReset={users.userToReset}
        newPassword={users.newPassword}
        setNewPassword={users.setNewPassword}
        onClose={() => {
          users.setShowPasswordResetModal(false);
          users.setNewPassword('');
          users.setUserToReset(null);
        }}
        handleResetPassword={users.handlePasswordReset}
        isLoading={users.isLoadingUsers}
      />

      {/* Edit Team Modal */}
      <EditTeamModal
        editingTeam={teams.editingTeam}
        setEditingTeam={teams.setEditingTeam}
        editTeamForm={teams.editTeamForm}
        setEditTeamForm={teams.setEditTeamForm}
        assignableUsers={tickets.assignableUsers}
        user={user}
        handleEditTeam={teams.handleEditTeam}
        isLoading={teams.isLoadingTeams}
      />

      {/* Add Team Member Modal */}
      <AddTeamMemberModal
        isOpen={teams.showAddMemberModal}
        team={teams.teamToAddMember}
        memberToAdd={teams.memberToAdd}
        setMemberToAdd={teams.setMemberToAdd}
        assignableUsers={tickets.assignableUsers}
        onClose={() => {
          teams.setShowAddMemberModal(false);
          teams.setMemberToAdd('');
          teams.setTeamToAddMember(null);
        }}
        handleAddTeamMember={teams.handleAddTeamMember}
        isLoading={teams.isLoadingTeams}
      />
    </div>
  );
};

export default App;